import { useState, useEffect } from 'react';
import { EmployerNavigation } from '../../components/EmployerNavigation';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import {
  Mail,
  Lock,
  Save,
  AlertCircle,
  Check,
  Eye,
  EyeOff,
  Building2,
  Shield,
  LogOut,
  Webhook,
} from 'lucide-react';

export function Settings() {
  const { user, profile, signOut } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [slackWebhook, setSlackWebhook] = useState('');
  const [outlookWebhook, setOutlookWebhook] = useState('');

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      loadEmployerSettings();
    }
  }, [user, profile]);

  useEffect(() => {
    validateEmail(email);
  }, [email]);

  useEffect(() => {
    validateNewPassword(newPassword);
  }, [newPassword]);

  useEffect(() => {
    validateConfirmPassword(confirmPassword);
  }, [confirmPassword, newPassword]);

  const loadEmployerSettings = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('employer_settings')
        .select('*')
        .eq('employer_id', profile.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        setSlackWebhook(data.slack_webhook_url || '');
        setOutlookWebhook(data.outlook_webhook_url || '');
      }
    } catch (error) {
      console.error('Error loading employer settings:', error);
    }
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError('Email is required');
    } else if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
    } else if (!value.includes('.')) {
      setEmailError('Email must contain a domain extension (e.g., .com)');
    } else {
      setEmailError('');
    }
  };

  const validateNewPassword = (value: string) => {
    if (value && value.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
    } else if (value && !/(?=.*[a-z])(?=.*[A-Z])/.test(value)) {
      setPasswordError('Password must contain both uppercase and lowercase letters');
    } else if (value && !/(?=.*\d)/.test(value)) {
      setPasswordError('Password must contain at least one number');
    } else {
      setPasswordError('');
    }
  };

  const validateConfirmPassword = (value: string) => {
    if (value && value !== newPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleUpdateEmail = async () => {
    if (emailError || !email) {
      setMessage({ type: 'error', text: 'Please fix email errors before updating' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        email: email,
      });

      if (error) throw error;

      await supabase
        .from('profiles')
        .update({ email: email })
        .eq('id', profile!.id);

      setMessage({ type: 'success', text: 'Email updated successfully! Check your new email for verification.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update email' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (passwordError || confirmPasswordError) {
      setMessage({ type: 'error', text: 'Please fix password errors before updating' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update password' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIntegrations = async () => {
    if (!profile) return;

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase
        .from('employer_settings')
        .upsert({
          employer_id: profile.id,
          slack_webhook_url: slackWebhook,
          outlook_webhook_url: outlookWebhook,
        });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Integration settings updated successfully!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update integrations' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <EmployerNavigation />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Employer Settings</h1>
          <p className="text-gray-600">Manage your account settings and integrations</p>
        </div>

        {message && (
          <div
            className={`mb-6 p-4 rounded-xl border-2 ${
              message.type === 'success'
                ? 'bg-green-50 border-green-300 text-green-900'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}
          >
            <div className="flex items-start gap-3">
              {message.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="font-semibold">{message.text}</p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-8 h-8" />
              <h3 className="text-lg font-bold">Company</h3>
            </div>
            <div className="text-2xl font-bold mb-1">{profile?.company_name || 'Your Company'}</div>
            <p className="text-white/80 text-sm">Employer Account</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8" />
              <h3 className="text-lg font-bold">Account Status</h3>
            </div>
            <div className="text-2xl font-bold mb-1">Active</div>
            <p className="text-white/80 text-sm">Good standing</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-cyan-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Email Settings</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition ${
                    emailError
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-cyan-500'
                  }`}
                  placeholder="your@company.com"
                />
                {emailError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {emailError}
                  </p>
                )}
              </div>

              <button
                onClick={handleUpdateEmail}
                disabled={loading || !!emailError || email === user?.email}
                className="w-full px-6 py-3 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Update Email
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Password Settings</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:outline-none pr-12"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none pr-12 transition ${
                      passwordError
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-300 focus:border-cyan-500'
                    }`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {passwordError}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none pr-12 transition ${
                      confirmPasswordError
                        ? 'border-red-500 focus:border-red-600'
                        : 'border-gray-300 focus:border-cyan-500'
                    }`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {confirmPasswordError}
                  </p>
                )}
              </div>

              <button
                onClick={handleUpdatePassword}
                disabled={
                  loading ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  !!passwordError ||
                  !!confirmPasswordError
                }
                className="w-full px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Lock className="w-5 h-5" />
                Update Password
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Webhook className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Integrations</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Slack Webhook URL
              </label>
              <input
                type="url"
                value={slackWebhook}
                onChange={(e) => setSlackWebhook(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="https://hooks.slack.com/services/..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Receive notifications in Slack when candidates apply
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Outlook Webhook URL
              </label>
              <input
                type="url"
                value={outlookWebhook}
                onChange={(e) => setOutlookWebhook(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="https://outlook.office.com/webhook/..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Receive notifications in Microsoft Teams
              </p>
            </div>

            <button
              onClick={handleUpdateIntegrations}
              disabled={loading}
              className="w-full px-6 py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Integrations
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border-2 border-red-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Sign Out</h2>
          </div>

          <p className="text-gray-600 mb-6">
            Sign out of your account. You'll need to sign back in to access your profile.
          </p>

          <button
            onClick={async () => {
              await signOut();
              window.location.href = '/login';
            }}
            className="w-full px-6 py-4 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition flex items-center justify-center gap-3 shadow-lg"
          >
            <LogOut className="w-6 h-6" />
            Sign Out of ResuSwipe
          </button>
        </div>
      </div>
    </div>
  );
}
